import type {CSSProperties} from 'react';
import type {AssistantTableResponse} from 'src/api/ProactApiClient';
import {
  AssistantChartUnitEnum,
  AssistantTableColumnFormatEnum,
} from 'src/api/ProactApiClient';
import {
  CHART_EMPTY_VALUE,
  formatChartValue,
} from 'src/app/assistant/Chart/chatChartFormat';
import {
  formatCount,
  formatUtcDateTime,
  formatUtcShortDate,
} from 'src/shared/utils/formatters';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

export type ChatTableSortDirection = 'asc' | 'desc';
export type ChatTableRow = readonly (string | null)[];

export const TABLE_COLUMN_MIN_WIDTH = 96;
export const NUMERIC_CELL_STYLE: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const FORMAT_UNITS: Partial<
  Record<AssistantTableColumnFormatEnum, AssistantChartUnitEnum>
> = {
  [AssistantTableColumnFormatEnum.Number]: AssistantChartUnitEnum.Number,
  [AssistantTableColumnFormatEnum.Count]: AssistantChartUnitEnum.Count,
  [AssistantTableColumnFormatEnum.Hours]: AssistantChartUnitEnum.Hours,
  [AssistantTableColumnFormatEnum.Currency]: AssistantChartUnitEnum.Currency,
  [AssistantTableColumnFormatEnum.Percent]: AssistantChartUnitEnum.Percent,
};

/** The chart unit a numeric table format formats like, or null for Text and Date. */
export const tableFormatUnit = (
  format: AssistantTableColumnFormatEnum
): AssistantChartUnitEnum | null => FORMAT_UNITS[format] ?? null;

export const isNumericTableFormat = (
  format: AssistantTableColumnFormatEnum
): boolean => tableFormatUnit(format) !== null;

/** True for a DateTime cell (ISO with a time part); DateOnly cells are `yyyy-MM-dd`. */
export const isDateTimeCell = (value: string | null): value is string =>
  value !== null && /^\d{4}-\d{2}-\d{2}T/.test(value);

const parseTableNumber = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const parseTableDate = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const date = DATE_ONLY.test(value)
    ? new Date(`${value}T00:00:00Z`)
    : parseUtcDate(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isUtcMidnight = (date: Date): boolean =>
  date.getUTCHours() === 0 &&
  date.getUTCMinutes() === 0 &&
  date.getUTCSeconds() === 0 &&
  date.getUTCMilliseconds() === 0;

const formatTableDate = (value: string): string => {
  const date = parseTableDate(value);
  if (!date) return value;
  return !isDateTimeCell(value) || isUtcMidnight(date)
    ? formatUtcShortDate(date)
    : formatUtcDateTime(date);
};

/**
 * Display text for one Scout table cell by its column format; numeric formats reuse the chart formatter.
 * @registry Format-aware display text for a Scout table cell (on-screen table, transcript export, print).
 */
export const formatTableCell = (
  value: string | null,
  format: AssistantTableColumnFormatEnum
): string => {
  if (value === null) return CHART_EMPTY_VALUE;
  const unit = tableFormatUnit(format);
  if (unit !== null) {
    const number = parseTableNumber(value);
    return number === null ? value : formatChartValue(number, unit);
  }
  return format === AssistantTableColumnFormatEnum.Date
    ? formatTableDate(value)
    : value;
};

/** The table's rows with every cell a string or null: the API sends explicit nulls the client types omit. */
export const normalizeTableRows = (
  table: AssistantTableResponse
): ChatTableRow[] =>
  (table.rows ?? []).map(row =>
    (row ?? []).map((cell: unknown) => (typeof cell === 'string' ? cell : null))
  );

type SortKey = number | string | null;

const sortKey = (
  value: string | null,
  format: AssistantTableColumnFormatEnum
): SortKey => {
  if (value === null) return null;
  if (isNumericTableFormat(format)) return parseTableNumber(value);
  if (format === AssistantTableColumnFormatEnum.Date) {
    return parseTableDate(value)?.getTime() ?? null;
  }
  return value;
};

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const compareKeys = (a: number | string, b: number | string): number =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : collator.compare(String(a), String(b));

/** The rows ordered by one column, ties in original order and missing values last either way. */
export const sortTableRows = <Row extends ChatTableRow>(
  rows: readonly Row[],
  columnIndex: number,
  format: AssistantTableColumnFormatEnum,
  direction: ChatTableSortDirection
): Row[] =>
  rows
    .map((row, index) => ({
      row,
      index,
      key: sortKey(row[columnIndex] ?? null, format),
    }))
    .sort((a, b) => {
      if (a.key === null || b.key === null) {
        if (a.key === b.key) return a.index - b.index;
        return a.key === null ? 1 : -1;
      }
      const order = compareKeys(a.key, b.key);
      if (order === 0) return a.index - b.index;
      return direction === 'asc' ? order : -order;
    })
    .map(({row}) => row);

export const TABLE_EMPTY_TEXT = 'No rows.';
export const TABLE_SORTED_NOTE = 'Sorting applies to these rows only.';

export const tableTruncationText = (rowCount: number): string =>
  rowCount === 0
    ? 'The result was too large to show here.'
    : `Showing the first ${formatCount(rowCount)} rows.`;

export interface TableDataGrid {
  header: string[];
  numeric: boolean[];
  rows: string[][];
}

/** A table's rows as display strings in the server's order (transcript export and print). */
export const tableDataGrid = (table: AssistantTableResponse): TableDataGrid => {
  const columns = table.columns ?? [];
  return {
    header: columns.map(column => column.label),
    numeric: columns.map(column => isNumericTableFormat(column.format)),
    rows: normalizeTableRows(table).map(row =>
      columns.map((column, index) =>
        formatTableCell(row[index] ?? null, column.format)
      )
    ),
  };
};
